import type {
	OrgInvoice,
	SeatCounts,
	SeatTopUpQuote,
	SetupQuote,
} from "@/types/api";
import { http } from "./http";

/**
 * Seat purchases and invoice history.
 *
 * `quote` is deliberately a separate read: an admin sees the exact figure
 * before committing, and the server produces it with the same code that
 * charges them, so the number cannot drift between the two.
 */
export const billingService = {
	quoteSeats: (additional: number, signal?: AbortSignal) =>
		http.get<{ quote: SeatTopUpQuote; seats: SeatCounts }>(
			"/api/org/billing/seats/quote",
			{ query: { additional }, signal },
		),

	/**
	 * `expectedCurrentSeats` is the count the quote was based on. If it has
	 * moved the server returns 409 rather than charging against a price the
	 * admin never saw — which also makes a double-clicked confirm harmless.
	 */
	buySeats: (additional: number, expectedCurrentSeats: number | null) =>
		http.post<{
			quote: SeatTopUpQuote;
			invoice: { id: string; payment_url: string | null };
			seats: SeatCounts;
		}>("/api/org/billing/seats", {
			additional,
			...(expectedCurrentSeats == null ? {} : { expectedCurrentSeats }),
		}),

	listInvoices: (signal?: AbortSignal) =>
		http.get<{ invoices: OrgInvoice[] }>("/api/org/billing/invoices", {
			signal,
		}),

	/**
	 * Ask the payment provider what actually happened to this invoice.
	 *
	 * "I paid this — why does it still say due?" A payment confirmation can be
	 * missed in transit, and an admin should not be stuck waiting on our inbox
	 * to catch up with money that has already left their account.
	 */
	/**
	 * Price a seat count for an org that has not started yet.
	 *
	 * Side-effect free, so an admin can try numbers and codes freely before
	 * committing to anything at all.
	 */
	setupQuote: (seats: number, code?: string, signal?: AbortSignal) =>
		http.get<SetupQuote>("/api/org/billing/setup/quote", {
			query: { seats, ...(code ? { code } : {}) },
			signal,
		}),

	/**
	 * Start the contract: fix the seats, apply the code, issue invoice one.
	 *
	 * billingEmail is sent with it rather than saved separately, because issuing
	 * that first invoice is what needs it — an org without one cannot be
	 * invoiced, and this request is where that would surface.
	 */
	completeSetup: (seats: number, code?: string, billingEmail?: string) =>
		http.post<{
			quote: SetupQuote;
			invoice: { id: string; payment_url: string | null };
		}>("/api/org/billing/setup", {
			seats,
			...(code ? { code } : {}),
			...(billingEmail ? { billingEmail } : {}),
		}),

	/**
	 * Set the seat count for the NEXT renewal.
	 *
	 * null clears a pending change, which is not the same as scheduling today's
	 * number — an admin who deliberately chose to stay put should keep that
	 * intent when they later buy a seat.
	 */
	setScheduledSeats: (seats: number | null) =>
		http.put<{ seats_scheduled: number | null }>(
			"/api/org/billing/seats/scheduled",
			{ seats },
		),

	refreshInvoice: (id: string) =>
		http.post<{ remoteStatus: string | null; action: string }>(
			`/api/org/billing/invoices/${id}/refresh`,
			{},
		),
};
